import { createGlobalStyle } from 'styled-components';

import { cpBrand, cpLight, cpDark } from './cp-tokens';

/**
 * Global styles for the CleverParking redesign (v2). Mounted only when the
 * user is on the new UI version (see `src/app/app.js`). Defines the `--cp-*`
 * CSS custom properties (light/dark), swaps the body typeface to IBM Plex,
 * and ships the parking identity primitives (asphalt header, road markings,
 * entry gate) ported 1:1 from the caps-reports reference.
 */
export const CpGlobalStyle = createGlobalStyle`
  :root {
    --cp-brd-radius: 14px;
    --cp-green: ${cpBrand.green};
    --cp-green-bright: ${cpBrand.greenBright};
    --cp-green-dark: ${cpBrand.greenDark};
    /* Accent used to recolor legacy blue controls to brand green in v2.
       In v1 this var is undefined, so components fall back to the old blue. */
    --cp-accent: ${cpBrand.green};
    --cp-accent-hover: #3aa863;
    /* Modern button system for v2 (v1 keeps its legacy pill via the fallbacks):
       rounded-rect, taller, semibold, soft shadow + top sheen, and a tactile
       hover-lift / active-press micro-interaction. */
    --cp-btn-radius: 10px;
    --cp-btn-height: 34px;
    /* heavier weight + tiny tracking + a dark text-shadow make the white label
       read crisp on the medium-green primary fill (was blёкло/muddy) */
    --cp-btn-weight: 700;
    --cp-btn-letter-spacing: 0.02em;
    --cp-btn-text-shadow: none; //0 1px 2px rgba(8, 14, 12, 0.45);
    /* glossy top sheen → deeper bottom edge; lifts white-label contrast on
       coloured buttons so the text no longer reads muddy */
    --cp-btn-overlay: linear-gradient(180deg, rgba(255, 255, 255, 0.14) 0%, rgba(255, 255, 255, 0) 42%, rgba(0, 0, 0, 0.14) 100%);
    --cp-btn-shadow: 0 1px 2px rgba(16, 24, 32, 0.1), inset 0 1px 0 rgba(255, 255, 255, 0.16);
    --cp-btn-shadow-hover: 0 5px 16px rgba(16, 24, 32, 0.16), inset 0 1px 0 rgba(255, 255, 255, 0.24);
    --cp-btn-hover-filter: brightness(1.05);
    --cp-btn-hover-transform: translateY(-1px);
    --cp-btn-active-transform: translateY(0) scale(0.98);
    --cp-asphalt: ${cpBrand.asphalt};
    --cp-asphalt-soft: ${cpBrand.asphaltSoft};
    --cp-gradient: ${cpBrand.gradient};

    --cp-bg: ${(p) => (p.theme.theme === 'dark' ? cpDark.bg : cpLight.bg)};
    --cp-surface: ${(p) => (p.theme.theme === 'dark' ? cpDark.surface : cpLight.surface)};
    --cp-surface-alt: ${(p) => (p.theme.theme === 'dark' ? cpDark.surfaceAlt : cpLight.surfaceAlt)};
    --cp-surface-hover: ${(p) => (p.theme.theme === 'dark' ? cpDark.surfaceHover : cpLight.surfaceHover)};
    --cp-text: ${(p) => (p.theme.theme === 'dark' ? cpDark.text : cpLight.text)};
    --cp-text-secondary: ${(p) => (p.theme.theme === 'dark' ? cpDark.textSecondary : cpLight.textSecondary)};
    --cp-text-tertiary: ${(p) => (p.theme.theme === 'dark' ? cpDark.textTertiary : cpLight.textTertiary)};
    --cp-border: ${(p) => (p.theme.theme === 'dark' ? cpDark.border : cpLight.border)};
    --cp-border-soft: ${(p) => (p.theme.theme === 'dark' ? cpDark.borderSoft : cpLight.borderSoft)};
    --cp-tag-bg: ${(p) => (p.theme.theme === 'dark' ? cpDark.tagBg : cpLight.tagBg)};
    --cp-scroll-thumb: ${(p) => (p.theme.theme === 'dark' ? cpDark.scrollThumb : cpLight.scrollThumb)};
    --cp-band-alt: ${(p) => (p.theme.theme === 'dark' ? cpDark.bandAlt : cpLight.bandAlt)};
    --cp-suspicious: ${(p) => (p.theme.theme === 'dark' ? cpDark.suspicious : cpLight.suspicious)};
    /* Muted text for inactive grid rows (blocked / archive / …). Status is
       encoded by TEXT brightness, not by a row background — one consistent
       grey per theme so every grid dims identically. */
    --cp-row-muted: ${(p) => (p.theme.theme === 'dark' ? '#9aa3a9' : '#727b81')};
    /* black table icons (locks) vanish on dark — invert them to white there */
    --cp-lock-filter: ${(p) => (p.theme.theme === 'dark' ? 'invert(1)' : 'none')};
  }

  body {
    font-family: 'IBM Plex Sans', -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif;
    background: var(--cp-bg);
    color: var(--cp-text);
    transition: background-color 0.18s ease, color 0.18s ease;
    -webkit-font-smoothing: antialiased;
    -moz-osx-font-smoothing: grayscale;
  }

  /* Brand scrollbar */
  ::-webkit-scrollbar { width: 8px; height: 8px; }
  ::-webkit-scrollbar-thumb { background: var(--cp-scroll-thumb); border-radius: 4px; }
  ::-webkit-scrollbar-thumb:hover { background: var(--cp-text-tertiary); }
  ::-webkit-scrollbar-track { background: transparent; }

  @keyframes cp-pulse {
    0%, 100% { opacity: 1; transform: scale(1); }
    50% { opacity: 0.45; transform: scale(0.85); }
  }

  /* ==== Entry gate (login page) — pure CSS ============================== */
  .cp-gate { position: relative; width: 300px; height: 210px; flex: none; }
  .cp-gate-rack {
    position: absolute; left: 26px; bottom: 18px;
    width: 74px; height: 148px; border-radius: 12px 12px 4px 4px;
    background: linear-gradient(180deg, #232d29 0%, #101714 100%);
    box-shadow: inset 0 0 0 1.5px rgba(92, 201, 138,0.22), 0 12px 24px rgba(0,0,0,0.45);
  }
  .cp-gate-rack::before {
    content: ''; position: absolute; left: 12px; top: 14px; right: 12px; height: 34px;
    border-radius: 6px;
    background: linear-gradient(135deg, rgba(92, 201, 138,0.85), rgba(0,155,0,0.85));
    box-shadow: 0 0 14px rgba(92, 201, 138,0.45);
  }
  .cp-gate-rack::after {
    content: ''; position: absolute; left: 16px; bottom: 26px; right: 16px; height: 6px;
    border-radius: 3px; background: #060a08;
    box-shadow: inset 0 1px 2px rgba(0,0,0,0.8), 0 1px 0 rgba(255,255,255,0.06);
  }
  .cp-gate-eye {
    position: absolute; left: 50%; top: 62px; transform: translateX(-50%);
    width: 12px; height: 12px; border-radius: 50%;
    background: #5cc98a; box-shadow: 0 0 10px 2px rgba(92, 201, 138,0.65);
    animation: cp-pulse 2.4s ease-in-out infinite;
  }
  .cp-gate-post {
    position: absolute; left: 150px; bottom: 18px;
    width: 34px; height: 86px; border-radius: 8px 8px 3px 3px;
    background: linear-gradient(180deg, #212a26 0%, #0f1512 100%);
    box-shadow: inset 0 0 0 1.5px rgba(92, 201, 138,0.18), 0 10px 20px rgba(0,0,0,0.4);
  }
  .cp-gate-post::before {
    content: ''; position: absolute; left: 50%; top: 10px; transform: translateX(-50%);
    width: 10px; height: 10px; border-radius: 50%;
    background: #ff5b4d; box-shadow: 0 0 9px 2px rgba(255, 91, 77, 0.6);
    transition: background 0.4s ease, box-shadow 0.4s ease;
  }
  .cp-gate-open .cp-gate-post::before {
    background: #5cc98a; box-shadow: 0 0 9px 2px rgba(92, 201, 138, 0.7);
  }
  .cp-gate-arm {
    position: absolute; left: 164px; bottom: 92px;
    width: 128px; height: 11px; border-radius: 6px;
    transform-origin: 6px 50%;
    background: repeating-linear-gradient(115deg, #f2f6f3 0 16px, #3aa863 16px 32px);
    box-shadow: 0 3px 8px rgba(0,0,0,0.45), inset 0 0 0 1px rgba(0,0,0,0.25);
    transition: transform 1.3s cubic-bezier(0.35, 1.25, 0.35, 1);
  }
  .cp-gate-open .cp-gate-arm { transform: rotate(-62deg); }
  .cp-gate-ground {
    position: absolute; left: 0; right: 0; bottom: 0; height: 20px; border-radius: 4px;
    background:
      repeating-linear-gradient(90deg, rgba(92, 201, 138,0.75) 0 20px, transparent 20px 34px) center 9px / 100% 2px no-repeat,
      linear-gradient(180deg, #131a16 0%, #0a0f0c 100%);
  }

  @media (max-width: 719px) {
    .cp-login-left { display: none !important; }
  }

  /* ==== v2 data tables ================================================== */
  /* Applied via the .cp-table marker on the table container (TablePlace /
     GridContainer). Colors come from --cp-* vars so light/dark follow the
     theme automatically. Selectors are kept specific enough to win over the
     legacy styled-components rules. */
  .cp-table {
    background: var(--cp-surface);
    border: 1px solid var(--cp-border);
    border-radius: 12px;
    box-shadow: none;
    /* NB: no overflow here — TablePlace keeps its own overflow-x:auto, and
       GridContainer stays overflow:visible (as in v1), so the redesign does
       not introduce an extra inner scroll container. */
  }
  .cp-table table {
    background: transparent;
  }
  .cp-table thead th {
    height: 42px;
    background: var(--cp-surface-alt);
    color: var(--cp-text-secondary);
    font-size: 12px;
    font-weight: 600;
    border-bottom: 1px solid var(--cp-border);
  }
  .cp-table thead.sticky,
  .cp-table thead.sticky th {
    background: var(--cp-surface-alt);
    border-bottom: 1px solid var(--cp-border);
  }
  .cp-table thead.sticky {
    box-shadow: 0 6px 16px rgba(0, 0, 0, 0.06);
  }
  .cp-table tbody td {
    height: 44px;
    font-size: 13px;
    color: var(--cp-text);
    border-bottom: 1px solid var(--cp-border-soft);
  }
  /* One flat surface — NO zebra and NO per-status row fills. Status is encoded
     by text brightness only (below), so blocked/archive rows never get their own
     background that "tears" the table apart. The explicit status selectors here
     also override the legacy dark styled <tr> rules (bg #4d5156 on archive/
     blocked, border-bottom #6c7277) which otherwise win on specificity. */
  .cp-table tbody tr,
  .cp-table tbody tr:nth-child(2n),
  .cp-table tbody tr.active,
  .cp-table tbody tr.blocked,
  .cp-table tbody tr.archive,
  .cp-table tbody tr.archived,
  .cp-table tbody tr.inactive,
  .cp-table tbody tr.disabled,
  .cp-table tbody tr.blocked_by_client {
    background: transparent;
    border: none;
  }
  .cp-table tbody tr:hover {
    background: var(--cp-surface-hover);
  }
  .cp-table tbody tr:last-child td {
    border-bottom: none;
  }
  /* Active / unspecified rows (incl. grids that emit no status class, e.g.
     discounts) read at full text brightness. */
  .cp-table tbody tr td,
  .cp-table tbody tr td a,
  .cp-table tbody tr.active td,
  .cp-table tbody tr.active td a {
    color: var(--cp-text);
  }
  /* Inactive statuses — ONE consistent muted grey, applied identically across
     every grid (tariffs: archive; discounts / payment means / clients / plates /
     RFID: status; parking sessions: archive). Same status => same brightness. */
  .cp-table tbody tr.blocked td,
  .cp-table tbody tr.blocked td a,
  .cp-table tbody tr.archive td,
  .cp-table tbody tr.archive td a,
  .cp-table tbody tr.archived td,
  .cp-table tbody tr.archived td a,
  .cp-table tbody tr.inactive td,
  .cp-table tbody tr.inactive td a,
  .cp-table tbody tr.disabled td,
  .cp-table tbody tr.disabled td a,
  .cp-table tbody tr.blocked_by_client td,
  .cp-table tbody tr.blocked_by_client td a {
    color: var(--cp-row-muted);
  }
  /* Suspicious rows: muted, deep red text (dimmed, not neon) + left marker.
     Declared last so it wins for rows that are both suspicious and archive. */
  .cp-table tbody tr.is_suspicious td,
  .cp-table tbody tr.is_suspicious td a {
    color: var(--cp-suspicious);
  }
  .cp-table.cp-table a {
    color: var(--cp-text);
    text-decoration: none;
  }
  .cp-table.cp-table a:hover {
    color: var(--cp-green);
    text-decoration: none;
  }

  /* Table lock icon: white on dark, black on light (per --cp-lock-filter). */
  .cp-table img[src*='black_lock_icon'] {
    filter: var(--cp-lock-filter);
  }

  /* ==== v2 checkboxes — modern custom control ==========================
     Rounded square, subtle border when empty, brand-green fill + crisp white
     check when ticked, light-green dash when indeterminate. Toggle switches
     (label.toggle > input) are explicitly reverted below so they keep their
     switch look. Radios stay native, tinted green. */
  /* :not(.toggle input) leaves switch inputs (label.toggle > input) fully alone. */
  input[type='checkbox']:not(.toggle input) {
    appearance: none;
    -webkit-appearance: none;
    -moz-appearance: none;
    box-sizing: border-box;
    width: 18px;
    height: 18px;
    margin: 0 6px 0 0;
    border: 1.5px solid var(--cp-border);
    border-radius: 5px;
    background-color: var(--cp-surface);
    background-repeat: no-repeat;
    background-position: center;
    background-size: 100% 100%;
    cursor: pointer;
    vertical-align: middle;
    transition: background-color 0.15s ease, border-color 0.15s ease, box-shadow 0.15s ease;
  }
  input[type='checkbox']:not(.toggle input):hover {
    border-color: var(--cp-green);
  }
  input[type='checkbox']:not(.toggle input):focus-visible {
    outline: none;
    box-shadow: 0 0 0 3px rgba(71, 185, 114, 0.25);
  }
  input[type='checkbox']:not(.toggle input):checked {
    background-color: var(--cp-green);
    border-color: var(--cp-green);
    background-image: url("data:image/svg+xml;utf8,<svg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 16 16'><path fill='none' stroke='%23fff' stroke-width='2.2' stroke-linecap='round' stroke-linejoin='round' d='M3.5 8.4l3 3 6-6.6'/></svg>");
  }
  input[type='checkbox']:not(.toggle input):indeterminate {
    background-color: #7ad17a;
    border-color: #7ad17a;
    background-image: url("data:image/svg+xml;utf8,<svg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 16 16'><path fill='none' stroke='%23fff' stroke-width='2.2' stroke-linecap='round' d='M4 8h8'/></svg>");
  }
  input[type='radio'] {
    accent-color: var(--cp-green);
  }

  /* Custom appearance:none checkboxes (form ".checkbox" and multiselect ".multi")
     paint a blue PNG when checked — repaint them brand green, with a lighter
     green indeterminate (partial "Все") state. */
  .checkbox input[type='checkbox'],
  .multi input[type='checkbox'] {
    border: 1px solid var(--cp-border) !important;
    background-color: var(--cp-surface) !important;
    background-image: none !important;
  }
  .checkbox input[type='checkbox']:checked,
  .multi input[type='checkbox']:checked {
    background-color: var(--cp-green) !important;
    border-color: var(--cp-green) !important;
    background-image: url("data:image/svg+xml;utf8,<svg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 16 16'><path fill='none' stroke='%23fff' stroke-width='2.4' stroke-linecap='round' stroke-linejoin='round' d='M3.4 8.3l3 3 6.2-6.5'/></svg>") !important;
    background-size: 100% 100% !important;
    background-repeat: no-repeat !important;
  }
  .multi input[type='checkbox']:indeterminate {
    background-color: #7ad17a !important;
    border-color: #7ad17a !important;
    background-image: url("data:image/svg+xml;utf8,<svg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 16 16'><path stroke='%23fff' stroke-width='2.4' stroke-linecap='round' d='M4 8h8'/></svg>") !important;
    background-size: 100% 100% !important;
    background-repeat: no-repeat !important;
  }

  /* ==== v2 native file-select button — rounded, green ================= */
  input[type='file']::file-selector-button,
  input[type='file']::-webkit-file-upload-button {
    border: none;
    border-radius: 999px;
    padding: 7px 16px;
    margin-right: 10px;
    background: var(--cp-green);
    color: #fff;
    font: inherit;
    font-size: 13px;
    cursor: pointer;
    transition: filter 0.15s ease;
  }
  input[type='file']::file-selector-button:hover,
  input[type='file']::-webkit-file-upload-button:hover {
    filter: brightness(1.05);
  }

  /* ==== v2 native <select> — one consistent chevron pinned to the END of the
     row (never mid-line), with room so the chosen value never clips under it.
     Forced so every form/filter select matches regardless of local styling. */
  select {
    -webkit-appearance: none !important;
    -moz-appearance: none !important;
    appearance: none !important;
    background-image: url("data:image/svg+xml;utf8,<svg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 16 16'><path fill='none' stroke='%23888888' stroke-width='1.6' stroke-linecap='round' stroke-linejoin='round' d='M4 6l4 4 4-4'/></svg>") !important;
    background-repeat: no-repeat !important;
    background-position: right 9px center !important;
    background-size: 14px !important;
    padding-right: 28px !important;
    text-overflow: ellipsis;
  }

  /* ==== v2 filter panel + form inputs ================================== */
  .cp-filter.cp-filter {
    background: var(--cp-surface);
    border: 1px solid var(--cp-border);
    border-radius: 14px;
    box-shadow: none;
  }
  .cp-filter.cp-filter .custom-select-value{
    height: 40px;
  }
  .cp-filter.cp-filter label {
    color: var(--cp-text-secondary);
  }
  /* Text-like fields only — never checkbox/radio (toggles are checkboxes and
     must keep their own switch styling). All fields share ONE background (the
     surface, like the date fields) and never change it on focus/fill — some
     legacy field components (autocomplete, masked, number) hard-code #fff, so
     force it. */
  .cp-filter.cp-filter input:not([type='checkbox']):not([type='radio']),
  .cp-filter.cp-filter select,
  .cp-filter.cp-filter textarea,
  .cp-filter.cp-filter .input {
    background: var(--cp-surface) !important;
    color: var(--cp-text);
    border: 1px solid var(--cp-border);
    border-radius: 10px;
  }
  
  /* Chrome autofill paints its own light background — override it */
  .cp-filter.cp-filter input:-webkit-autofill,
  .cp-filter.cp-filter input:-webkit-autofill:hover,
  .cp-filter.cp-filter input:-webkit-autofill:focus {
    -webkit-box-shadow: 0 0 0 100px var(--cp-surface) inset !important;
    -webkit-text-fill-color: var(--cp-text) !important;
    caret-color: var(--cp-text);
  }
  /* One consistent CP focus highlight for EVERY filter control — text inputs,
     native selects, textareas here, and the custom dropdowns (CustomSelect /
     MultiSelect) which use the same green border + 3px/0.15 ring on open/focus.
     Keep this ring value in sync with those two components. */
  .cp-filter.cp-filter input:not([type='checkbox']):not([type='radio']):focus,
  .cp-filter.cp-filter select:focus,
  .cp-filter.cp-filter textarea:focus {
    border-color: var(--cp-green);
    box-shadow: 0 0 0 3px rgba(71, 185, 114, 0.15);
    outline: none;
  }
  .cp-filter.cp-filter input::placeholder {
    color: var(--cp-text-tertiary);
  }

  .cp-filter input::placeholder {
    color: #ff4500; 
    opacity: 1;    
  }


  /* NB: toast notification (react-notifications-component) overrides live in
     src/index.css so they apply in BOTH UI versions (index.css is always
     loaded, CpGlobalStyle only in v2). They still consume the --cp-* vars
     defined above when v2 is active. */

  /* ==== v2 calendar (rc-calendar) ====================================== */
  /* Lift the popup above the filter fields (fixes the overlap) and repaint
     the cyan accents in brand green; light surface theming for dark mode. */
  .rc-calendar-picker.rc-calendar-picker { z-index: 4000; }
  .rc-calendar.rc-calendar { z-index: 4000; }

  .rc-calendar.rc-calendar {
    background: var(--cp-surface);
    border: 1px solid var(--cp-border);
    border-radius: var(--cp-radius-lg, 14px);
    color: var(--cp-text);
    box-shadow: 0 14px 40px rgba(0, 0, 0, 0.22);
    overflow: hidden;
  }
  /* The actual visible surface. The base calendar.css paints it solid #fff and
     absolutely positions it with an invalid \`right:1;top:1\` — that left the
     calendar WHITE in dark mode (Реклама / Клиенты / СО) and different from the
     redux-form popup. Repaint it with the CP surface in BOTH themes and let it
     flow as a clean dropdown so every calendar in the app looks identical. */
  .rc-calendar .rc-calendar-panel {
    background: transparent;
  }
  .rc-calendar .rc-calendar-date-panel {
    position: relative;
    right: auto;
    top: auto;
    background: var(--cp-surface);
    color: var(--cp-text);
    box-shadow: none;
  }
  /* Shrink the popup to its content width — the date grid and the time-picker
     are both ~300px, so the default oversized panel left a big empty gap on the
     right when picking time. */
  .rc-calendar.calendar,
  .rc-calendar.rc-calendar .rc-calendar-panel,
  .rc-calendar.rc-calendar .rc-calendar-date-panel {
    width: 302px;
    min-width: 0;
  }
  /* date-input row (shown by some pickers) + range middle strip */
  .rc-calendar .rc-calendar-input-wrap {
    border-color: var(--cp-border-soft);
  }
  .rc-calendar .rc-calendar-input {
    background: var(--cp-surface);
    color: var(--cp-text);
  }
  .rc-calendar .rc-calendar-header,
  .rc-calendar .rc-calendar-footer {
    border-color: var(--cp-border-soft);
    color: var(--cp-text);
  }
  .rc-calendar .rc-calendar-header a,
  .rc-calendar .rc-calendar-column-header-inner {
    color: var(--cp-text-secondary);
  }
  .rc-calendar .rc-calendar-date {
    color: var(--cp-text);
  }
  .rc-calendar .rc-calendar-last-month-cell .rc-calendar-date,
  .rc-calendar .rc-calendar-next-month-btn-day .rc-calendar-date {
    color: var(--cp-text-tertiary);
  }
  /* Body grows to fit all 6 week-rows (never overlaps the footer). The base
     theme's fixed 217px was too short for 6-week months. */
  .rc-calendar .rc-calendar-body {
    height: auto;
  }
  .rc-calendar .rc-calendar-date:hover {
    background: var(--cp-surface-hover);
  }
  /* today: only a green ring — do NOT recolor the text, or a day that is both
     today and selected would render green-on-green and vanish */
  .rc-calendar .rc-calendar-today .rc-calendar-date {
    border-color: var(--cp-green);
  }
  /* selected range endpoints and single selection */
  .rc-calendar .rc-calendar-selected-day .rc-calendar-date {
    background: rgba(71, 185, 114, 0.14);
  }
  .rc-calendar .rc-calendar-selected-date .rc-calendar-date,
  .rc-calendar .rc-calendar-selected-date .rc-calendar-date:hover,
  .rc-calendar .rc-calendar-selected-start-date .rc-calendar-date,
  .rc-calendar .rc-calendar-selected-end-date .rc-calendar-date {
    background: var(--cp-green);
    color: #fff;
  }
  /* month / year / decade / time panels */
  .rc-calendar-month-panel-selected-cell .rc-calendar-month-panel-month,
  .rc-calendar-month-panel-selected-cell .rc-calendar-month-panel-month:hover,
  .rc-calendar-year-panel-selected-cell .rc-calendar-year-panel-year,
  .rc-calendar-year-panel-selected-cell .rc-calendar-year-panel-year:hover,
  .rc-calendar-decade-panel-selected-cell .rc-calendar-decade-panel-decade,
  .rc-calendar-decade-panel-selected-cell .rc-calendar-decade-panel-decade:hover,
  .rc-calendar-time-panel-selected-cell .rc-calendar-time-panel-time,
  .rc-calendar-time-panel-selected-cell .rc-calendar-time-panel-time:hover {
    background: var(--cp-green);
    color: #fff;
  }
  .rc-calendar-month-panel,
  .rc-calendar-year-panel,
  .rc-calendar-decade-panel,
  .rc-calendar-time-panel {
    background: var(--cp-surface);
    color: var(--cp-text);
  }
  .rc-calendar-time-panel-cell .rc-calendar-time-panel-inner {
    color: var(--cp-text);
  }
  .rc-calendar-time-panel-cell .rc-calendar-time-panel-inner:hover {
    background: var(--cp-surface-hover);
  }
  /* footer confirm / today buttons */
  .rc-calendar .rc-calendar-ok-btn,
  .rc-calendar .rc-calendar-time-picker-btn {
    color: var(--cp-accent);
  }
  /* time-picker column (rc-time-picker) — selected time green, not blue */
  .rc-calendar-time-picker-panel .rc-time-picker-panel-select li.rc-time-picker-panel-select-option-selected,
  li.rc-time-picker-panel-select-option-selected {
    background: var(--cp-green);
    color: #fff;
  }
  .rc-calendar-time-picker-panel .rc-time-picker-panel-select li:hover,
  .rc-time-picker-panel-select li:hover {
    background: var(--cp-surface-hover);
  }
  /* Time-picker overlay stretches to exactly cover the (now auto-height) body:
     from just below the header (top:34) to just above the footer — so the hour
     columns fill the full height and nothing overlaps, at any body height. */
  .rc-calendar-time-picker {
    background: var(--cp-surface);
    top: 34px;
    bottom: 40px;
    height: auto;
  }
  /* The rc-time-picker Panel root has a fixed 170px width in the base css, so
     its two columns filled only the left ~half and left a big empty block on
     the right. Force it to fill the calendar width. */
  .rc-calendar-time-picker-panel .rc-time-picker-panel,
  .rc-calendar-time-picker-panel .rc-time-picker-panel-narrow {
    width: 100%;
    max-width: none;
  }
  .rc-calendar-time-picker-panel,
  .rc-calendar-time-picker-panel .rc-time-picker-panel-inner,
  .rc-calendar-time-picker-panel .rc-time-picker-panel-select {
    height: 100%;
  }
  /* The inner was an inline-block sized to its two columns (~300px), so with
     the seconds column hidden it left a blank strip on the right. Make it fill
     the panel width and split the remaining space evenly between hours/minutes
     so there is no empty block while the time picker is open. */
  .rc-calendar-time-picker-panel .rc-time-picker-panel-inner {
    display: block;
    width: 100%;
    background: var(--cp-surface);
    color: var(--cp-text);
    border: none;
    box-shadow: none;
  }
  .rc-calendar-time-picker-panel .rc-time-picker-panel-combobox {
    display: flex;
    width: 100%;
    height: 100%;
  }
  .rc-calendar-time-picker-panel .rc-time-picker-panel-select ul {
    max-height: 100%;
  }
  .rc-calendar-time-picker-panel .rc-time-picker-panel-select {
    flex: 1 1 0;
    width: auto;
    float: none;
    border-color: var(--cp-border-soft);
  }
  .rc-calendar-time-picker-panel .rc-time-picker-panel-select li {
    text-align: center;
    padding: 0;
    color: var(--cp-text);
  }
  .rc-time-picker-panel-select li {
    color: var(--cp-text);
  }
  /* calendar header/nav links + time-input focus were cyan (#23c0fa) — green */
  .rc-calendar-header > a:hover,
  .rc-calendar-prev-month-btn:hover,
  .rc-calendar-next-month-btn:hover,
  .rc-calendar-prev-year-btn:hover,
  .rc-calendar-next-year-btn:hover,
  .rc-calendar-month-panel-header > a:hover,
  .rc-calendar-year-panel-header > a:hover,
  .rc-calendar-decade-panel-header > a:hover,
  .rc-calendar-day-select:hover,
  .rc-calendar-time-picker-btn:hover {
    color: var(--cp-accent);
  }
  .rc-calendar-time-input:focus {
    border-color: var(--cp-accent);
    box-shadow: 0 0 3px var(--cp-accent);
  }
`;
